import type { ChangeEvent, Dispatch } from 'react';

import type { SurveyQuestion } from '@/api/surveys';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { DEFAULT_RATING_CONFIG, MAX_RATING_VALUES } from '../constants';
import type { BuilderAction } from '../builderReducer';

interface RatingEditorProps {
    dispatch: Dispatch<BuilderAction>;
    question: SurveyQuestion;
    questionId: string;
}

const parseNumber = (value: string, fallback: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeStep = (value: number) => Math.max(1, value);

const getMaxAllowedRatingValue = (min: number, step: number) =>
    min + step * (MAX_RATING_VALUES - 1);

const clampRatingMax = (min: number, max: number, step: number) => {
    const minimumMax = min + step;
    const maximumMax = getMaxAllowedRatingValue(min, step);

    return Math.min(Math.max(max, minimumMax), maximumMax);
};

export function RatingEditor({ dispatch, question, questionId }: RatingEditorProps) {
    const rating = question.config?.rating ?? DEFAULT_RATING_CONFIG;
    const step = normalizeStep(rating.step);
    const maxAllowedRatingValue = getMaxAllowedRatingValue(rating.min, step);

    return (
        <div className="grid gap-4 rounded-2xl border border-border/70 bg-surface/30 p-4 sm:grid-cols-3">
            <div className="space-y-2">
                <Label>Mínimo</Label>
                <Input
                    type="number"
                    value={rating.min}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const min = parseNumber(event.target.value, rating.min);

                        dispatch({
                            type: 'updateRating',
                            questionId,
                            patch: {
                                min,
                                max: clampRatingMax(min, rating.max, step),
                            },
                        });
                    }}
                />
            </div>
            <div className="space-y-2">
                <Label>Máximo</Label>
                <Input
                    type="number"
                    min={rating.min + step}
                    max={maxAllowedRatingValue}
                    value={rating.max}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const max = parseNumber(event.target.value, rating.max);

                        dispatch({
                            type: 'updateRating',
                            questionId,
                            patch: {
                                max: clampRatingMax(rating.min, max, step),
                            },
                        });
                    }}
                />
                <p className="text-xs text-muted-foreground">
                    Máximo permitido com o mínimo e passo atuais: {maxAllowedRatingValue}.
                </p>
            </div>
            <div className="space-y-2">
                <Label>Passo</Label>
                <Input
                    type="number"
                    min="1"
                    value={rating.step}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const nextStep = normalizeStep(
                            parseNumber(event.target.value, rating.step),
                        );

                        dispatch({
                            type: 'updateRating',
                            questionId,
                            patch: {
                                step: nextStep,
                                max: clampRatingMax(rating.min, rating.max, nextStep),
                            },
                        });
                    }}
                />
            </div>
            <div className="space-y-2 sm:col-span-3 sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0">
                <div className="space-y-2">
                    <Label>Rótulo esquerdo</Label>
                    <Input
                        value={rating.left_label ?? ''}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            dispatch({
                                type: 'updateRating',
                                questionId,
                                patch: { left_label: event.target.value },
                            })
                        }
                        placeholder="Ex.: Pouco"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Rótulo direito</Label>
                    <Input
                        value={rating.right_label ?? ''}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            dispatch({
                                type: 'updateRating',
                                questionId,
                                patch: { right_label: event.target.value },
                            })
                        }
                        placeholder="Ex.: Muito"
                    />
                </div>
            </div>
        </div>
    );
}
